import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { Settings } from './Settings';
import { Updater } from './Updater';

import { Buffer } from 'buffer';
import * as cmd from './Commands';
import { firmwareSourceForHardware } from './FirmwareRepository';
import { AbsType, IdentResponse } from './generated/wss_pb';
import { StatusView } from './StatusView';
import { isAck } from './ProtobufCommand';
import { TestMode } from './TestMode';
import { Diagnostic } from './Diagnostic';
import { ServiceRoutines } from './ServiceRoutines';

const NoSerial = (): JSX.Element => (<div className='uiSection warning'>This browser is not supported, as it isn't able to communicate with your Mk60 Companion. Please use a recent version of Google Chrome or Microsoft Edge instead.</div>);

const InfoUi = (): JSX.Element => (
    <div className='uiSection'>
        <div><a href="https://happycactusgarage.com/">Happy Cactus Garage Store</a></div>
        <div><a href="https://github.com/mck1117/abs-docs/blob/main/Mk60-Companion/Setup-Guide.md">MK60 Companion Instructions</a></div>
        <div><a href="https://discord.gg/hQUz5Vgk9Z">Discord Community</a></div>
    </div>);

enum ConnectionState {
    Disconnected = 'Disconnected',
    ConnectedBootloader = 'Connected to Bootloader',
    ConnectedFirmware = 'Connected to Firmware',
};

const supportsSerial = "serial" in navigator;

export const App = (): JSX.Element => {
    const isMfgMode = window.location.search ? (window.location.search.indexOf('mfgmode')) > 0 : false;

    const [serialPort, setSerialPort] = useState<SerialPort | undefined>();
    const serialPortRef = useRef(serialPort);
    serialPortRef.current = serialPort;

    const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
    const [controllerIdent, setControllerIdent] = useState<IdentResponse>();
    const [disconnectReason, setDisconnectReason] = useState<string>('');

    const availableFirmware = firmwareSourceForHardware(controllerIdent?.getHardwarerevision() ?? 0);
    const updateIsAvailable =
                        availableFirmware &&
                        controllerIdent &&
                        !controllerIdent.getIsbootloader() &&
                        (availableFirmware.swVersion > controllerIdent.getVersion());

    // True if connected with a valid serial port and controller ident
    const isConnected = !!(connectionState !== ConnectionState.Disconnected && serialPort && controllerIdent);

    // Request that the user select a serial port
    const findPort = useCallback(async () => {
        if (supportsSerial) {
            try {
                const port = await navigator.serial.requestPort();
                await port.open({ baudRate: 115200 });
                setDisconnectReason('');
                setSerialPort(port);
            } catch {
                // swallow error, user canceled or something
            }
        }
    }, []);

    // Disconnect/cleanup handler
    const onDisconnect = useCallback(async (reason: string) => {
        const port = serialPortRef.current;
        if (port) {
            serialPortRef.current = undefined;
            setSerialPort(undefined);
            setConnectionState(ConnectionState.Disconnected);
            setControllerIdent(undefined);
            setDisconnectReason(reason);

            try { await port.close(); } catch { /* port may already be gone */ }
            try { await port.forget(); } catch { /* port may already be gone */ }
        }
    }, []);

    // Listen for USB disconnect events
    useEffect(() => {
        if (!supportsSerial || !serialPort) return;

        const handleDisconnect = (event: Event) => {
            if (event.target === serialPort) {
                onDisconnect('USB device was disconnected');
            }
        };

        navigator.serial.addEventListener('disconnect', handleDisconnect);
        return () => navigator.serial.removeEventListener('disconnect', handleDisconnect);
    }, [serialPort, onDisconnect]);

    // When we get a new serial port, probe whether it's openblt or firmware
    useEffect(() => {
        if (serialPort) {
            (async () => {
                const resp = await cmd.identify(serialPort, onDisconnect);

                if (resp?.hasIdent()) {
                    const ident = resp?.getIdent()!;
                    setControllerIdent(ident);

                    if (ident.getIsbootloader()) {
                        setConnectionState(ConnectionState.ConnectedBootloader);
                    } else {
                        setConnectionState(ConnectionState.ConnectedFirmware);
                    }
                } else {
                    onDisconnect('Failed to identify device');
                }
            })();
        }
    }, [serialPort, onDisconnect]);

    const onRebootToLoader = useCallback(async () => {
        const response = await cmd.rebootToBootloader(serialPort!, onDisconnect);
        if (isAck(response)) {
            // only do disconnect if ack - otherwise stay in fw update
            await onDisconnect('Rebooting...');
            findPort();
        }
    }, [serialPort, onDisconnect, findPort]);

    const absTypeRef = useRef<AbsType>(AbsType.GENERICONOFF);

    const connGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 12px', alignItems: 'center', justifyItems: 'start', justifyContent: 'start' };
    const connLabelStyle: React.CSSProperties = { justifySelf: 'end' };
    const connFullRow: React.CSSProperties = { gridColumn: '1 / -1' };

    return (
        <div className='uiRoot'>
            <div className='uiSection'>
                <img alt='Happy Cactus Garage' src={process.env.PUBLIC_URL + '/HappyCactusGarage_header.png'} style={{ marginLeft: 'auto', marginRight: 'auto', display: 'block', width: '100%' }} />
                <h1 style={{ marginTop: '12px' }}>MK60 Companion Tool</h1>
            </div>
            <InfoUi />
            {!supportsSerial && (<NoSerial />)}
            {supportsSerial &&
                (<>
                    <div className='uiSection'>
                        <h2>Connection</h2>
                        <div style={connGridStyle}>
                            <div style={connFullRow}>
                                {isConnected ?
                                    (<>
                                        <button type='button' onClick={() => onDisconnect('')}>Disconnect</button>
                                        {controllerIdent.getIsbootloader() ?
                                            (<button type='button' onClick={onRebootToLoader}>Reboot to app</button>) :
                                            (<button type='button' onClick={onRebootToLoader}>Reboot to bootloader</button>)}
                                    </>) : (<button type='button' onClick={findPort}>Connect</button>)}
                            </div>
                            <span style={connLabelStyle}>State</span>
                            <span>{connectionState}</span>
                            {!isConnected && disconnectReason && (<>
                                <span style={connLabelStyle} />
                                <span className='warning'>{disconnectReason}</span>
                            </>)}
                            {controllerIdent && (<>
                                <span style={connLabelStyle}>Software</span>
                                <span>{controllerIdent.getVersion()}</span>
                            </>)}
                            {updateIsAvailable && (<>
                                <span style={connLabelStyle} />
                                <span className='warning'>Software update available: {availableFirmware.swVersion}</span>
                            </>)}
                            {controllerIdent && (<>
                                <span style={connLabelStyle}>Hardware ID</span>
                                <span>{controllerIdent.getHardwarerevision()}</span>
                            </>)}
                            {controllerIdent && (<>
                                <span style={connLabelStyle}>Converter type</span>
                                <span>{controllerIdent.getHasswitchablehallvr() ? "Hall/VR" : "VR only"}</span>
                            </>)}
                            {controllerIdent && !availableFirmware && (<>
                                <span style={connLabelStyle} />
                                <span className='warning'>Hardware ID not recognized, please contact support.</span>
                            </>)}
                            {controllerIdent && (<>
                                <span style={connLabelStyle}>SN</span>
                                <span>{Buffer.from(controllerIdent.getSerialnumber()).toString('hex')}</span>
                            </>)}
                        </div>
                    </div>
                    {isConnected && connectionState === ConnectionState.ConnectedFirmware && (
                        <div className='uiSection'>
                            <StatusView port={serialPort!} onDisconnect={onDisconnect} absTypeRef={absTypeRef} isMfgMode={isMfgMode} />
                        </div>
                    )}
                    {isConnected && (
                        <div className='uiSection'>
                            <div>
                                {connectionState === ConnectionState.ConnectedFirmware
                                    ? (<Settings port={serialPort!} onDisconnect={onDisconnect} absTypeRef={absTypeRef} isMfgMode={isMfgMode} controllerIdent={controllerIdent!} />)
                                    : availableFirmware
                                        ? (<Updater port={serialPort!} firmwareLocation={availableFirmware.file} controllerIdent={controllerIdent!} onDisconnect={onDisconnect} />)
                                        : (<>
                                            <h2>Firmware Update Error</h2>
                                            <div className="warning">No firmware update is available for your device with hardware ID {controllerIdent?.getHardwarerevision()}. Please contact support.</div>
                                        </>)
                                }
                            </div>
                        </div>
                    )}
                    {isConnected && connectionState === ConnectionState.ConnectedFirmware && (
                        <div className='uiSection'>
                            <TestMode port={serialPort!} onDisconnect={onDisconnect} />
                        </div>
                    )}
                    {isConnected && connectionState === ConnectionState.ConnectedFirmware && (
                        <div className='uiSection'>
                            <Diagnostic port={serialPort!} onDisconnect={onDisconnect} />
                        </div>
                    )}
                    {isConnected && connectionState === ConnectionState.ConnectedFirmware && (
                        <div className='uiSection'>
                            <ServiceRoutines port={serialPort!} onDisconnect={onDisconnect} />
                        </div>
                    )}
                </>)
            }
        </div>);
}

export default App;
